"use strict";
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
// External packages
const debounce_1 = __importDefault(require("lodash/debounce"));
const react_1 = __importDefault(require("react"));
const client_1 = require("react-dom/client");
const react_i18next_1 = require("react-i18next");
const turf_1 = require("@turf/turf");
const Preferences_1 = __importDefault(require("chaire-lib-common/lib/config/Preferences"));
const ServiceLocator_1 = __importDefault(require("chaire-lib-common/lib/utils/ServiceLocator"));
const GlobalMapEvents_1 = __importDefault(require("chaire-lib-frontend/lib/services/map/events/GlobalMapEvents"));
const MapLayerManager_1 = __importDefault(require("chaire-lib-frontend/lib/services/map/MapLayerManager"));
const MapPopupManager_1 = __importDefault(require("chaire-lib-frontend/lib/services/map/MapPopupManager"));
// Local workspace imports
const layers_config_1 = __importStar(require("../../config/layers.config"));
const PolygonSelectionService_1 = require("../../services/map/PolygonSelectionService");
const events_1 = __importDefault(require("../../services/map/events"));
const MapRelatedCustomEvents_1 = __importDefault(require("../../services/map/events/MapRelatedCustomEvents"));
const PathMapLayerManager_1 = __importDefault(require("../../services/map/PathMapLayerManager"));
const transitNodesUtils_1 = require("../../services/transitNodes/transitNodesUtils");
const MapRenderer_1 = __importDefault(require("./MapRenderer"));
const MapCursorHelper_1 = require("../../services/map/MapCursorHelper");
const projectBaseMapLayers_1 = require("../../config/projectBaseMapLayers");
/**
 * TODO: For now, hard code the map for Transition here. But it should be in
 * chaire-lib and offer the possibility to pass the application modules when the
 * API for it has stabilised.
 */
class MainMap extends react_1.default.Component {
    constructor(props) {
        super(props);
        this.fitBounds = (coordinates) => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            if (map) {
                map.fitBounds(coordinates, {
                    padding: 20,
                    bearing: map.getBearing()
                });
            }
        };
        this.setCenter = (coordinates) => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            map === null || map === void 0 ? void 0 : map.setCenter(coordinates);
        };
        this.onEnableBoxZoom = () => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            map === null || map === void 0 ? void 0 : map.boxZoom.enable();
        };
        this.onDisableBoxZoom = () => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            map === null || map === void 0 ? void 0 : map.boxZoom.disable();
        };
        this.onEnableDragPan = () => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            map === null || map === void 0 ? void 0 : map.dragPan.enable();
        };
        this.onDisableDragPan = () => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            map === null || map === void 0 ? void 0 : map.dragPan.disable();
        };
        this.onMapError = (e) => {
            console.error('Map error:', e);
            if (!this.state.mapLoaded) {
                // Even if there was a map error, call the map.loaded event so the
                // application can continue loading
                this.setState({ mapLoaded: true });
                ServiceLocator_1.default.eventManager.emit('map.loaded');
            }
        };
        this.setMap = () => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            if (!map)
                return;
            this.map = map;
            this.layerManager.setMap(map);
            this.popupManager.setMap(map);
            this.layerManager.updateEnabledLayers(this.state.layers);
            this.setState({ mapLoaded: true });
            ServiceLocator_1.default.eventManager.emit('map.loaded');
        };
        this.showPathsByAttribute = (attribute, value) => {
            // attribute must be agency_id or line_id
            if (attribute === 'agency_id') {
                this.pathLayerManager.showAgencyId(value);
            }
            else if (attribute === 'line_id') {
                this.pathLayerManager.showLineId(value);
            }
        };
        this.hidePathsByAttribute = (attribute, value) => {
            // attribute must be agency_id or line_id
            if (attribute === 'agency_id') {
                this.pathLayerManager.hideAgencyId(value);
            }
            else if (attribute === 'line_id') {
                this.pathLayerManager.hideLineId(value);
            }
        };
        this.clearPathsFilter = () => {
            this.pathLayerManager.clearFilter();
        };
        this.componentDidMount = () => {
            // Map initialization is now handled by react-map-gl component
            // We'll set up event handlers after map loads
            ServiceLocator_1.default.addService('layerManager', this.layerManager);
            ServiceLocator_1.default.addService('pathLayerManager', this.pathLayerManager);
            MapRelatedCustomEvents_1.default.addEvents(ServiceLocator_1.default.eventManager);
            ServiceLocator_1.default.eventManager.on('map.updateEnabledLayers', this.updateEnabledLayers);
            ServiceLocator_1.default.eventManager.onEvent('map.updateLayer', this.updateLayer);
            const contextMenu = document.getElementById('tr__main-map-context-menu');
            this.setState({
                contextMenu,
                contextMenuRoot: contextMenu ? (0, client_1.createRoot)(contextMenu) : undefined
            });
            ServiceLocator_1.default.eventManager.on('map.updateLayers', this.updateLayers);
            ServiceLocator_1.default.eventManager.on('map.addPopup', this.addPopup);
            ServiceLocator_1.default.eventManager.on('map.removePopup', this.removePopup);
            ServiceLocator_1.default.eventManager.on('map.updateFilter', this.updateFilter);
            ServiceLocator_1.default.eventManager.on('map.clearFilter', this.clearFilter);
            ServiceLocator_1.default.eventManager.on('map.showLayer', this.showLayer);
            ServiceLocator_1.default.eventManager.on('map.hideLayer', this.hideLayer);
            ServiceLocator_1.default.eventManager.on('map.paths.byAttribute.show', this.showPathsByAttribute);
            ServiceLocator_1.default.eventManager.on('map.paths.byAttribute.hide', this.hidePathsByAttribute);
            ServiceLocator_1.default.eventManager.on('map.paths.clearFilter', this.clearPathsFilter);
            ServiceLocator_1.default.eventManager.on('map.fitBounds', this.fitBounds);
            ServiceLocator_1.default.eventManager.on('map.setCenter', this.setCenter);
            ServiceLocator_1.default.eventManager.on('map.enableBoxZoom', this.onEnableBoxZoom);
            ServiceLocator_1.default.eventManager.on('map.disableBoxZoom', this.onDisableBoxZoom);
            ServiceLocator_1.default.eventManager.on('map.enableDragPan', this.onEnableDragPan);
            ServiceLocator_1.default.eventManager.on('map.disableDragPan', this.onDisableDragPan);
            ServiceLocator_1.default.eventManager.on('map.showContextMenu', this.showContextMenu);
            ServiceLocator_1.default.eventManager.on('map.hideContextMenu', this.hideContextMenu);
            ServiceLocator_1.default.eventManager.on('map.handleDrawControl', this.handleDrawControl);
            ServiceLocator_1.default.eventManager.on('map.deleteSelectedNodes', this.deleteSelectedNodes);
            ServiceLocator_1.default.eventManager.on('map.deleteSelectedPolygon', this.onDeleteSelectedPolygon);
            ServiceLocator_1.default.eventManager.on('collection.update.nodes', this.onNodesUpdatedHandler);
        };
        this.onDeleteSelectedPolygon = () => {
            PolygonSelectionService_1.polygonSelectionService.deleteSelectedPolygon();
        };
        this.onNodesUpdatedHandler = () => {
            PolygonSelectionService_1.polygonSelectionService.onNodesUpdated();
        };
        this.componentWillUnmount = () => {
            var _a;
            // Reset cursor classes hover state to prevent stale state on remount
            (0, MapCursorHelper_1.resetClasses)();
            // Cancel any pending debounced save to avoid post-unmount side-effects
            this.debouncedSaveOverlayOpacity.cancel();
            ServiceLocator_1.default.removeService('layerManager');
            ServiceLocator_1.default.removeService('pathLayerManager');
            MapRelatedCustomEvents_1.default.removeEvents(ServiceLocator_1.default.eventManager);
            // Note: react-map-gl handles resize internally, so we don't need removeResizeListener
            ServiceLocator_1.default.eventManager.off('map.updateEnabledLayers', this.updateEnabledLayers);
            ServiceLocator_1.default.eventManager.off('map.updateLayer', this.updateLayer);
            ServiceLocator_1.default.eventManager.off('map.updateLayers', this.updateLayers);
            ServiceLocator_1.default.eventManager.off('map.addPopup', this.addPopup);
            ServiceLocator_1.default.eventManager.off('map.removePopup', this.removePopup);
            ServiceLocator_1.default.eventManager.off('map.updateFilter', this.updateFilter);
            ServiceLocator_1.default.eventManager.off('map.clearFilter', this.clearFilter);
            ServiceLocator_1.default.eventManager.off('map.showLayer', this.showLayer);
            ServiceLocator_1.default.eventManager.off('map.hideLayer', this.hideLayer);
            ServiceLocator_1.default.eventManager.off('map.paths.byAttribute.show', this.showPathsByAttribute);
            ServiceLocator_1.default.eventManager.off('map.paths.byAttribute.hide', this.hidePathsByAttribute);
            ServiceLocator_1.default.eventManager.off('map.paths.clearFilter', this.clearPathsFilter);
            ServiceLocator_1.default.eventManager.off('map.fitBounds', this.fitBounds);
            ServiceLocator_1.default.eventManager.off('map.setCenter', this.setCenter);
            ServiceLocator_1.default.eventManager.off('map.enableBoxZoom', this.onEnableBoxZoom);
            ServiceLocator_1.default.eventManager.off('map.disableBoxZoom', this.onDisableBoxZoom);
            ServiceLocator_1.default.eventManager.off('map.enableDragPan', this.onEnableDragPan);
            ServiceLocator_1.default.eventManager.off('map.disableDragPan', this.onDisableDragPan);
            ServiceLocator_1.default.eventManager.off('map.showContextMenu', this.showContextMenu);
            ServiceLocator_1.default.eventManager.off('map.hideContextMenu', this.hideContextMenu);
            ServiceLocator_1.default.eventManager.off('map.handleDrawControl', this.handleDrawControl);
            ServiceLocator_1.default.eventManager.off('map.deleteSelectedNodes', this.deleteSelectedNodes);
            ServiceLocator_1.default.eventManager.off('map.deleteSelectedPolygon', this.onDeleteSelectedPolygon);
            ServiceLocator_1.default.eventManager.off('collection.update.nodes', this.onNodesUpdatedHandler);
            // Remove map event listeners BEFORE react-map-gl cleans up
            // We need to do this early because react-map-gl will call map.remove() automatically
            // and if we try to remove listeners after that, it can cause errors
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            if (map && typeof map.off === 'function') {
                try {
                    map.off('style.load', this.onStyleChange);
                }
                catch (error) {
                    console.error('Error removing style.load event listener:', error);
                }
            }
            // react-map-gl automatically calls map.remove() during component unmount,
            // so we don't need to call it manually here
        };
        this.executeEvents = (e, events) => {
            if (e.originalEvent && e.originalEvent.cancelBubble === true) {
                return;
            }
            for (let eventIndex = 0; eventIndex < events.length; eventIndex++) {
                const event = events[eventIndex];
                if (event.condition === undefined || event.condition(this.props.activeSection)) {
                    event.handler(e);
                }
                if (e.originalEvent && e.originalEvent.cancelBubble === true) {
                    break;
                }
            }
        };
        this.getEventHandler = (events) => {
            return (e) => this.executeEvents(e, events);
        };
        this.showLayer = (layerName) => {
            this.layerManager.showLayer(layerName);
        };
        this.hideLayer = (layerName) => {
            this.layerManager.hideLayer(layerName);
        };
        this.clearFilter = (layerName) => {
            this.layerManager.clearFilter(layerName);
        };
        this.updateFilter = (layerName, filter) => {
            this.layerManager.updateFilter(layerName, filter);
        };
        this.deleteSelectedNodes = () => {
            this.setState({
                confirmModalDeleteIsOpen: true
            });
        };
        this.onDeleteSelectedNodes = () => {
            ServiceLocator_1.default.eventManager.emit('progress', { name: 'DeletingNodes', progress: 0.0 });
            const selectedNodes = ServiceLocator_1.default.selectedObjectsManager.getSelection('nodes');
            (0, transitNodesUtils_1.deleteUnusedNodes)(selectedNodes.map((n) => n.getId()))
                .then((_response) => {
                ServiceLocator_1.default.selectedObjectsManager.deselect('nodes');
                ServiceLocator_1.default.collectionManager.refresh('nodes');
                ServiceLocator_1.default.eventManager.emit('map.updateLayers', {
                    transitNodes: ServiceLocator_1.default.collectionManager.get('nodes').toGeojson(),
                    transitNodesSelected: (0, turf_1.featureCollection)([])
                });
            })
                .catch((error) => {
                // TODO Log errors
                console.error('Error deleting unused nodes', error);
            })
                .finally(() => {
                PolygonSelectionService_1.polygonSelectionService.deleteSelectedPolygon();
                ServiceLocator_1.default.eventManager.emit('progress', { name: 'DeletingNodes', progress: 1.0 });
            });
        };
        this.handleDrawControl = (section) => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            if (!map)
                return;
            PolygonSelectionService_1.polygonSelectionService.handleSectionChange(map, section);
        };
        this.addPopup = (popupId, popup, removeAll = true) => {
            this.hideContextMenu();
            if (removeAll) {
                this.removeAllPopups();
            }
            this.popupManager.addPopup(popupId, popup);
        };
        this.removePopup = (popupId) => {
            this.popupManager.removePopup(popupId);
        };
        this.removeAllPopups = () => {
            this.popupManager.removeAllPopups();
        };
        this.updateLayer = (args) => {
            this.layerManager.updateLayer(args.layerName, args.data);
        };
        this.updateLayers = (geojsonByLayerName) => {
            this.layerManager.updateLayers(geojsonByLayerName);
        };
        this.updateEnabledLayers = (enabledLayers) => {
            this.layerManager.updateEnabledLayers(enabledLayers);
        };
        this.showContextMenu = (e, elements) => {
            const contextMenu = this.state.contextMenu;
            if (!contextMenu || !this.state.contextMenuRoot) {
                return;
            }
            contextMenu.style.left = e.point.x + 'px';
            contextMenu.style.top = e.point.y + 'px';
            contextMenu.style.display = 'block';
            this.state.contextMenuRoot.render(react_1.default.createElement("ul", null, elements.map((element) => (react_1.default.createElement("li", { key: element.key ? element.key : element.title, style: { display: 'block', padding: '5px' }, onClick: () => {
                    element.onClick();
                    contextMenu.style.display = 'none';
                }, onMouseOver: () => element.onHover && element.onHover() }, this.props.t(element.title))))));
        };
        this.hideContextMenu = () => {
            if (!this.state.contextMenu || !this.state.contextMenuRoot) {
                return;
            }
            const contextMenu = this.state.contextMenu;
            contextMenu.style.display = 'none';
            this.state.contextMenuRoot.render(react_1.default.createElement(react_1.default.Fragment, null));
        };
        /**
         * Minimal style for OSM + dimming overlay. Project basemaps replace the whole style via `setStyle` in MapRenderer.
         */
        this.getMapStyle = () => {
            const sources = {
                overlay: {
                    type: 'geojson',
                    data: layers_config_1.overlaySource
                }
            };
            const layers = [];
            sources[projectBaseMapLayers_1.OSM_DEFAULT_LAYER.shortname] = {
                type: 'raster',
                tiles: [projectBaseMapLayers_1.OSM_DEFAULT_LAYER.url],
                tileSize: 256,
                attribution: (0, projectBaseMapLayers_1.formatRasterBasemapAttribution)(projectBaseMapLayers_1.OSM_DEFAULT_LAYER, this.props.t)
            };
            layers.push({
                id: projectBaseMapLayers_1.OSM_DEFAULT_LAYER.shortname,
                type: 'raster',
                source: projectBaseMapLayers_1.OSM_DEFAULT_LAYER.shortname
            });
            // Semi-transparent overlay to improve visibility of transit layers
            layers.push({
                id: 'base-overlay',
                type: 'fill',
                source: 'overlay',
                paint: {
                    'fill-color': this.state.overlayColor === 'white' ? '#ffffff' : '#000000',
                    'fill-opacity': this.state.overlayOpacity / 100
                }
            });
            return {
                version: 8,
                sources,
                layers
            };
        };
        /**
         * When the preferred basemap has a zoom range, show it only inside that range; otherwise show OSM.
         */
        this.handleZoomChange = (zoom) => {
            this.setState((prevState) => {
                let activeBasemapShortname = prevState.activeBasemapShortname;
                const pref = prevState.userPreferredBasemapShortname;
                const osmFallbackBounds = (0, projectBaseMapLayers_1.getZoomBoundsForOsMFallbackBasemap)(pref);
                if (osmFallbackBounds) {
                    const inR = zoom >= osmFallbackBounds.min && zoom <= osmFallbackBounds.max;
                    if (inR) {
                        activeBasemapShortname = pref;
                    }
                    else if (activeBasemapShortname === pref) {
                        activeBasemapShortname = projectBaseMapLayers_1.DEFAULT_FALLBACK_BASEMAP_SHORTNAME;
                    }
                }
                return {
                    currentZoom: zoom,
                    activeBasemapShortname
                };
            });
        };
        this.handleLayerChange = (layerType) => {
            const zoom = this.state.currentZoom;
            const bounds = (0, projectBaseMapLayers_1.getZoomBoundsForBasemapShortname)(layerType);
            let activeBasemapShortname = layerType;
            if (bounds && (zoom < bounds.min || zoom > bounds.max)) {
                activeBasemapShortname = projectBaseMapLayers_1.DEFAULT_FALLBACK_BASEMAP_SHORTNAME;
            }
            this.setState({
                userPreferredBasemapShortname: layerType,
                activeBasemapShortname
            });
            // Save preference to database (`map.basemapShortname` matches Preferences.get / defaultPreferences.map)
            Preferences_1.default.update({
                'map.basemapShortname': layerType
            }, ServiceLocator_1.default.socketEventManager);
        };
        /** Handle overlay opacity change from the slider (0–100) */
        this.handleOverlayOpacityChange = (opacity) => {
            // Update UI immediately for responsiveness
            this.setState({ overlayOpacity: opacity });
            // Debounced save to avoid flooding the server during slider drag
            this.debouncedSaveOverlayOpacity(opacity);
        };
        /** Handle overlay color change */
        this.handleOverlayColorChange = (color) => {
            this.setState({ overlayColor: color });
            // Save preference to database
            Preferences_1.default.update({
                'map.overlayColor': color
            }, ServiceLocator_1.default.socketEventManager);
        };
        /**
         * Start or stop the animation of the custom layers.
         * @param enabled Whether the custom layers are animated
         */
        this.setAnimationEnabled = (enabled) => {
            this.layerManager.setAnimationEnabled(enabled);
        };
        /**
         * Setup map event handlers after map loads
         */
        this.setupMapEvents = () => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            if (!map)
                return;
            for (const eventName in this.mapEvents) {
                for (const layerName in this.mapEvents[eventName]) {
                    if (layerName === 'map') {
                        map.on(eventName, this.getEventHandler(this.mapEvents[eventName][layerName]));
                    }
                    else {
                        // The 'as any' cast is required because MapLibre GL's TypeScript definitions
                        // don't support dynamic strings for the layer-specific on(type, layerName, handler) overload.
                        // The types expect a literal union type for the event name, but we iterate dynamically.
                        // Still an issue as of maplibre-gl v5.16.0 (verified Jan 2026).
                        map.on(eventName, layerName, this.getEventHandler(this.mapEvents[eventName][layerName]));
                    }
                }
            }
            map.on('error', this.onMapError);
            // Listen for style changes (when switching base layers)
            // and re-add the layers. 'style.load' fires once when the style is fully loaded,
            // unlike 'styledata' which fires multiple times during loading.
            map.on('style.load', this.onStyleChange);
            // If style is already loaded (e.g., on component re-render), call onStyleChange immediately.
            // This ensures controls are properly initialized at startup.
            if (map.isStyleLoaded()) {
                // Use setTimeout to ensure this runs after event listeners are set up
                setTimeout(() => {
                    this.onStyleChange();
                }, 0);
            }
        };
        /**
         * Handle map style changes (when switching between base layers)
         * Re-initialize layers and controls after style is loaded
         */
        this.onStyleChange = () => {
            var _a;
            const map = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
            if (!map)
                return;
            /**
             * `style.load` runs when the new style JSON is applied; `map.isStyleLoaded()` stays false until
             * tiles, updated sources, and sprites satisfy Style.loaded() in maplibre-gl. Running our
             * GeoJSON re-attach only after `isStyleLoaded()` avoids silent skips on vector basemaps; we
             * may need several `idle` callbacks before that becomes true.
             */
            const MAX_STYLE_READY_RETRIES = 20;
            let retries = 0;
            const tryApplyWhenReady = () => {
                var _a;
                const m = (_a = this.mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
                if (!m)
                    return;
                if (!m.isStyleLoaded()) {
                    retries++;
                    if (retries > MAX_STYLE_READY_RETRIES) {
                        console.warn(`Map style did not finish loading after ${MAX_STYLE_READY_RETRIES} idle cycles, proceeding anyway`);
                        ServiceLocator_1.default.eventManager.emit('progressClear', { name: 'MapStyleSwitch' });
                        return;
                    }
                    m.once('idle', tryApplyWhenReady);
                    return;
                }
                const currentEnabledLayers = this.layerManager.getEnabledLayers();
                this.layerManager.updateEnabledLayers(currentEnabledLayers.length > 0 ? currentEnabledLayers : this.state.layers);
                ServiceLocator_1.default.eventManager.emit('progress', { name: 'MapStyleSwitch', progress: 1.0 });
            };
            tryApplyWhenReady();
        };
        const savedBasemapShortname = Preferences_1.default.get('map.basemapShortname', undefined) || 'osm';
        const validNames = (0, projectBaseMapLayers_1.getValidBasemapShortnames)();
        const basemapShortname = validNames.includes(savedBasemapShortname)
            ? savedBasemapShortname
            : 'osm';
        // Get user's preferred overlay opacity from preferences (0–100, default 50)
        const overlayOpacity = Preferences_1.default.get('map.overlayOpacity', 50);
        // Get user's preferred overlay color from preferences (default 'black')
        const overlayColor = Preferences_1.default.get('map.overlayColor', 'black');
        const prefZoomBounds = (0, projectBaseMapLayers_1.getZoomBoundsForBasemapShortname)(basemapShortname);
        let initialActiveBasemapShortname = basemapShortname;
        if (prefZoomBounds && (props.zoom < prefZoomBounds.min || props.zoom > prefZoomBounds.max)) {
            initialActiveBasemapShortname = projectBaseMapLayers_1.DEFAULT_FALLBACK_BASEMAP_SHORTNAME;
        }
        this.state = {
            layers: layers_config_1.sectionLayers[this.props.activeSection] || [], // Get layers for section from config
            confirmModalDeleteIsOpen: false,
            mapLoaded: false,
            contextMenu: null,
            contextMenuRoot: undefined,
            currentZoom: props.zoom,
            userPreferredBasemapShortname: basemapShortname,
            activeBasemapShortname: initialActiveBasemapShortname,
            overlayOpacity,
            overlayColor
        };
        // Debounce the server save for overlay opacity (400ms) to avoid flooding on slider drag
        this.debouncedSaveOverlayOpacity = (0, debounce_1.default)((opacity) => {
            Preferences_1.default.update({ 'map.overlayOpacity': opacity }, ServiceLocator_1.default.socketEventManager);
        }, 400);
        this.defaultZoomArray = [props.zoom];
        this.defaultCenter = props.center;
        this.layerManager = new MapLayerManager_1.default(layers_config_1.default);
        this.pathLayerManager = new PathMapLayerManager_1.default(this.layerManager);
        this.popupManager = new MapPopupManager_1.default();
        this.mapEvents = {};
        this.map = undefined;
        this.mapRef = react_1.default.createRef();
        const newEvents = [GlobalMapEvents_1.default, events_1.default];
        const newEventsArr = newEvents.flatMap((ev) => ev);
        newEventsArr.forEach((eventDescriptor) => {
            this.mapEvents[eventDescriptor.eventName] = this.mapEvents[eventDescriptor.eventName] || {};
            if (eventDescriptor.type === 'layer') {
                const events = this.mapEvents[eventDescriptor.eventName][eventDescriptor.layerName] || [];
                events.push(eventDescriptor);
                this.mapEvents[eventDescriptor.eventName][eventDescriptor.layerName] = events;
            }
            else {
                const events = this.mapEvents[eventDescriptor.eventName]['map'] || [];
                events.push(eventDescriptor);
                this.mapEvents[eventDescriptor.eventName]['map'] = events;
            }
        });
    }
    render() {
        return (react_1.default.createElement(MapRenderer_1.default, { mapRef: this.mapRef, defaultCenter: this.defaultCenter, defaultZoom: this.defaultZoomArray[0], mapLoaded: this.state.mapLoaded, getMapStyle: this.getMapStyle, setAnimationEnabled: this.setAnimationEnabled, setupMapEvents: this.setupMapEvents, setMap: this.setMap, confirmModalDeleteIsOpen: this.state.confirmModalDeleteIsOpen, onDeleteSelectedNodes: this.onDeleteSelectedNodes, activeBasemapShortname: this.state.activeBasemapShortname, currentZoom: this.state.currentZoom, overlayOpacity: this.state.overlayOpacity, overlayColor: this.state.overlayColor, maxRasterBasemapZoom: (0, projectBaseMapLayers_1.getMaxConfiguredRasterBasemapZoom)(), handleZoomChange: this.handleZoomChange, onLayerChange: this.handleLayerChange, onOverlayOpacityChange: this.handleOverlayOpacityChange, onOverlayColorChange: this.handleOverlayColorChange, closeModal: () => this.setState({ confirmModalDeleteIsOpen: false }) }, this.props.children));
    }
}
exports.default = (0, react_i18next_1.withTranslation)(['main', 'transit'])(MainMap);
//# sourceMappingURL=TransitionMainMap.js.map